import { useCallback } from 'react';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';
import {
  generateScenePromptsFromText,
  type ProposedScene,
} from '@/services/batchPrompts';
import { createScene, patchScene } from '@/services/scenesApi';
import { runMutation } from '@/state/saveQueue';
import { toast } from '@/state/toastStore';
import type { Scene } from '@/types';

interface BatchInput {
  text: string;
  extractCast: boolean;
}

/**
 * Drives Batch mode: turns a chunk of text into N empty scenes appended
 * to the grid, with smart auto-link references already wired. No images
 * are generated here — Auto-play does that.
 */
export function useBatchGenerate() {
  return useCallback(async (episodeUuid: string, input: BatchInput) => {
    const result = await generateScenePromptsFromText(episodeUuid, input.text, {
      extractCast: input.extractCast,
    });

    if (result.cast.length > 0) {
      toast.info(
        `Cast review lands soon: ${result.cast.length} character${result.cast.length === 1 ? '' : 's'} suggested.`,
      );
    }

    const { scenes, upsertScene } = useEpisodeStore.getState();
    const { pendingReferences } = useSelectionStore.getState();
    const startOrder = (scenes.at(-1)?.order ?? 0) + 1;

    const createdScenes = await createScenesSequentially(
      episodeUuid,
      result.scenes,
      startOrder,
      pendingReferences.castIds,
    );

    for (const scene of createdScenes) upsertScene(scene);

    if (createdScenes.length > 0) {
      toast.ok(`${createdScenes.length} scenes created — hit Auto-play to render.`);
    }

    return createdScenes;
  }, []);
}

async function createScenesSequentially(
  episodeUuid: string,
  proposed: ProposedScene[],
  startOrder: number,
  activeCastIds: string[],
): Promise<Scene[]> {
  const created: Scene[] = [];
  // First pass — create all scenes WITHOUT scene references; we don't have
  // their IDs yet. Cast references are safe to apply now.
  for (let i = 0; i < proposed.length; i++) {
    const p = proposed[i];
    const scene = await runMutation(
      {
        episodeUuid,
        endpoint: '/v2/scenes',
        method: 'POST',
        body: { prompt: p.prompt, order: startOrder + i, references: { sceneIds: [], castIds: activeCastIds } },
      },
      () =>
        createScene(episodeUuid, {
          prompt: p.prompt,
          order: startOrder + i,
          references: { sceneIds: [], castIds: activeCastIds },
        }),
    );
    created.push(scene);
  }

  // Second pass — wire scene references now that we have IDs. Indices are
  // 1-based against the proposed array; clamp out-of-range silently.
  for (let i = 0; i < proposed.length; i++) {
    const indices = proposed[i].referencedSceneIndices ?? [];
    const sceneIds = indices
      .map((idx) => created[idx - 1]?.id)
      .filter((x): x is string => Boolean(x));
    if (sceneIds.length === 0) continue;

    const target: Scene = created[i];
    const updated: Scene = await runMutation(
      {
        episodeUuid,
        endpoint: `/v2/scenes/${target.id}`,
        method: 'PATCH',
        body: { references: { sceneIds, castIds: activeCastIds } },
        expectedVersion: target.version,
      },
      () =>
        patchScene(
          episodeUuid,
          target.id,
          { references: { sceneIds, castIds: activeCastIds } },
          target.version,
        ),
    );
    created[i] = updated;
  }

  return created;
}
